/**
 * Referral status tiers (bronze → diamond), mirrored from the recruiter bot's
 * `team-bot/src/rank.ts` so the level a user sees in the web app matches the one
 * the AI coach and the bot use. A tier is reached by hitting EITHER threshold —
 * some ambassadors convert a few high-value people (earnings), others bring many
 * small ones (referrals). Thresholds MUST stay in sync across services.
 */

export const RANKS = ['bronze', 'silver', 'gold', 'platinum', 'diamond'] as const;
export type Rank = (typeof RANKS)[number];

interface Tier {
  rank: Rank;
  minReferrals: number;
  /** GRAM (the TON-chain coin) of cumulative referral earnings. */
  minEarningsGram: number;
}

// Highest first, so the first match wins.
const TIERS: Tier[] = [
  { rank: 'diamond', minReferrals: 50, minEarningsGram: 500 },
  { rank: 'platinum', minReferrals: 20, minEarningsGram: 150 },
  { rank: 'gold', minReferrals: 8, minEarningsGram: 40 },
  { rank: 'silver', minReferrals: 3, minEarningsGram: 8 },
  { rank: 'bronze', minReferrals: 0, minEarningsGram: 0 },
];

/** Brand-consistent accent color for each tier badge. */
export const RANK_COLOR: Record<Rank, string> = {
  bronze: '#cd7f32',
  silver: '#c7ccd6',
  gold: '#f5c542',
  platinum: '#8fd4e8',
  diamond: '#7ef0ff',
};

/** Emoji used in the bot and as a compact badge glyph. */
export const RANK_EMOJI: Record<Rank, string> = {
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇',
  platinum: '💎',
  diamond: '💠',
};

export function rankIndex(rank: Rank): number {
  return RANKS.indexOf(rank);
}

export function calculateRank(referralsCount: number, earningsGram: number): Rank {
  for (const t of TIERS) {
    if (referralsCount >= t.minReferrals || earningsGram >= t.minEarningsGram) return t.rank;
  }
  return 'bronze';
}

export interface RankProgress {
  rank: Rank;
  next: Rank | null;
  /** Direct referrals still needed to reach the next rank (0 at the top). */
  referralsToNext: number;
  /** GRAM still needed to reach the next rank (0 at the top). */
  earningsToNext: number;
  /** 0..1 progress toward the next rank (the closer of the two metrics). */
  fraction: number;
}

/** Current rank plus what it takes to reach the next one. */
export function rankProgress(referralsCount: number, earningsGram: number): RankProgress {
  const rank = calculateRank(referralsCount, earningsGram);
  const idx = rankIndex(rank);
  const next = idx < RANKS.length - 1 ? RANKS[idx + 1] : null;
  if (!next) {
    return { rank, next: null, referralsToNext: 0, earningsToNext: 0, fraction: 1 };
  }
  const target = TIERS.find((t) => t.rank === next)!;
  const base = TIERS.find((t) => t.rank === rank)!;
  const referralsToNext = Math.max(0, target.minReferrals - referralsCount);
  const earningsToNext = Math.max(0, target.minEarningsGram - earningsGram);
  // Progress along whichever metric is furthest along (the one that will get
  // there first), clamped to [0, 1].
  const refSpan = target.minReferrals - base.minReferrals;
  const earnSpan = target.minEarningsGram - base.minEarningsGram;
  const refFrac = refSpan > 0 ? (referralsCount - base.minReferrals) / refSpan : 0;
  const earnFrac = earnSpan > 0 ? (earningsGram - base.minEarningsGram) / earnSpan : 0;
  const fraction = Math.max(0, Math.min(1, Math.max(refFrac, earnFrac)));
  return {
    rank,
    next,
    referralsToNext,
    earningsToNext: Math.round(earningsToNext),
    fraction,
  };
}
